import React, { useEffect, useState } from 'react'
import {
  Box, Chip, CircularProgress, Paper, Tab, Tabs, Typography,
} from '@mui/material'
import PeopleIcon       from '@mui/icons-material/PeopleOutlined'
import SchoolIcon       from '@mui/icons-material/SchoolOutlined'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'

interface ClassRow {
  class_id:           string
  name:               string
  enrolled_count:     number
  next_meeting_at:    string | null
  next_meeting_format: string | null
  next_meeting_zoom_id:  string | null
  next_meeting_zoom_pass: string | null
}

interface OneOnOneStudent {
  student_id:   string
  name:         string
  email:        string
  grade:        number | null
  current_step: number | null
  last_login:   string | null
}

function fmtNextMeeting(s: string) {
  return new Date(s).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: 'America/Los_Angeles',
  })
}
function timeSince(s: string | null) {
  if (!s) return 'never'
  const days = Math.floor((Date.now() - new Date(s).getTime()) / 864e5)
  if (days <= 0) return 'today'
  if (days === 1) return '1 day ago'
  if (days < 30) return `${days} days ago`
  return `${Math.floor(days / 30)}mo ago`
}

/**
 * Replaces the long inline-expandable student list on the mentor dashboard.
 *
 * Tab 1 — Classes I mentor (cards link to /classes/:id for class detail)
 * Tab 2 — 1:1 students (lead-mentees NOT in any class I mentor;
 *         cards link to /students for full management)
 *
 * Heavy student management lives on /students (where the canonical table is).
 * Per-class detail lives on /classes/:id. The dashboard surfaces the people
 * but doesn't try to be a management surface.
 */
export default function MyPeopleTabs() {
  const { session, role } = useAuth()
  const navigate          = useNavigate()
  const [tab,        setTab]        = useState<'classes' | 'oneOnOne'>('classes')
  const [classes,    setClasses]    = useState<ClassRow[]>([])
  const [oneOnOne,   setOneOnOne]   = useState<OneOnOneStudent[]>([])
  const [loading,    setLoading]    = useState(true)

  async function load() {
    if (!session) return
    setLoading(true)
    try {
      // ── Classes I mentor (admin/OM see all) ───────────────────────────────
      let classIds: string[] = []
      if (role === 'admin' || role === 'office_manager') {
        const { data: allClasses } = await supabase.from('class').select('class_id')
        classIds = (allClasses ?? []).map(c => c.class_id)
      } else {
        const { data: cm } = await supabase.from('class_mentor').select('class_id').eq('user_id', session.user.id)
        classIds = (cm ?? []).map(r => r.class_id)
      }

      let classRows: ClassRow[] = []
      if (classIds.length > 0) {
        const nowIso = new Date().toISOString()
        const [{ data: classData }, { data: enrollments }, { data: meetings }] = await Promise.all([
          supabase.from('class').select('class_id, name').in('class_id', classIds),
          supabase.from('class_enrollment').select('class_id').in('class_id', classIds).eq('status', 'active'),
          supabase.from('class_meeting')
            .select('class_id, scheduled_at, format, zoom_id, zoom_passcode')
            .in('class_id', classIds)
            .neq('status', 'cancelled')
            .gte('scheduled_at', nowIso)
            .order('scheduled_at'),
        ])
        const enrollMap: Record<string, number> = {}
        for (const e of enrollments ?? []) enrollMap[e.class_id] = (enrollMap[e.class_id] ?? 0) + 1
        const nextByClass: Record<string, any> = {}
        for (const m of meetings ?? []) {
          if (!nextByClass[m.class_id]) nextByClass[m.class_id] = m
        }
        classRows = (classData ?? []).map(c => {
          const next = nextByClass[c.class_id]
          return {
            class_id:           c.class_id,
            name:               c.name,
            enrolled_count:     enrollMap[c.class_id] ?? 0,
            next_meeting_at:    next?.scheduled_at ?? null,
            next_meeting_format: next?.format ?? null,
            next_meeting_zoom_id:  next?.zoom_id ?? null,
            next_meeting_zoom_pass: next?.zoom_passcode ?? null,
          }
        })
      }
      setClasses(classRows)

      // ── 1:1 students (lead-mentees NOT enrolled in any of my classes) ─────
      let oneOnOneRows: OneOnOneStudent[] = []
      if (role !== 'admin' && role !== 'office_manager') {
        const { data: leadAssignments } = await supabase
          .from('student_mentor')
          .select('student_id')
          .eq('mentor_id', session.user.id)
          .eq('mentor_role', 'lead')
          .eq('status', 'active')
        const leadStudentIds = (leadAssignments ?? []).map(r => r.student_id)
        if (leadStudentIds.length > 0) {
          // Drop students who are in any of my classes (those belong in the Classes tab)
          let inClassIds = new Set<string>()
          if (classIds.length > 0) {
            const { data: classEnrolls } = await supabase
              .from('class_enrollment')
              .select('student_id')
              .in('class_id', classIds)
              .in('student_id', leadStudentIds)
              .eq('status', 'active')
            for (const e of classEnrolls ?? []) inClassIds.add(e.student_id)
          }
          const orphanIds = leadStudentIds.filter(id => !inClassIds.has(id))
          if (orphanIds.length > 0) {
            const [{ data: profiles }, { data: projects }] = await Promise.all([
              supabase.from('student_profile').select('student_id, user_id, grade').in('student_id', orphanIds),
              supabase.from('project').select('student_id, current_step').in('student_id', orphanIds).eq('status', 'active'),
            ])
            const userIds = (profiles ?? []).map(p => p.user_id)
            const { data: users } = userIds.length > 0
              ? await supabase.from('users').select('user_id, first_name, last_name, email, last_login').in('user_id', userIds)
              : { data: [] as any[] }
            const userMap = Object.fromEntries((users ?? []).map(u => [u.user_id, u]))
            const stepMap = Object.fromEntries((projects ?? []).map(p => [p.student_id, p.current_step]))
            oneOnOneRows = (profiles ?? []).map(p => {
              const u = userMap[p.user_id] ?? {}
              return {
                student_id:   p.student_id,
                name:         `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || '—',
                email:        u.email ?? '',
                grade:        p.grade ?? null,
                current_step: stepMap[p.student_id] ?? null,
                last_login:   u.last_login ?? null,
              }
            })
            oneOnOneRows.sort((a, b) => a.name.localeCompare(b.name))
          }
        }
      }
      setOneOnOne(oneOnOneRows)
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [session, role])

  const showOneOnOneTab = role !== 'admin' && role !== 'office_manager'

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>My People</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate('/students')}
        >
          Open all students →
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, minHeight: 36 }}
      >
        <Tab
          value="classes"
          icon={<SchoolIcon fontSize="small" />}
          iconPosition="start"
          label={`Classes (${classes.length})`}
          sx={{ textTransform: 'none', minHeight: 36 }}
        />
        {showOneOnOneTab && (
          <Tab
            value="oneOnOne"
            icon={<PeopleIcon fontSize="small" />}
            iconPosition="start"
            label={`1:1 students (${oneOnOne.length})`}
            sx={{ textTransform: 'none', minHeight: 36 }}
          />
        )}
      </Tabs>

      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
      ) : tab === 'classes' ? (
        classes.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">
              No classes assigned yet.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.5 }}>
            {classes.map(c => (
              <Paper
                key={c.class_id}
                variant="outlined"
                sx={{ p: 2, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                onClick={() => navigate(`/classes/${c.class_id}`)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>{c.name}</Typography>
                  <Chip size="small" label={`${c.enrolled_count} student${c.enrolled_count !== 1 ? 's' : ''}`} />
                </Box>
                {c.next_meeting_at ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Next: {fmtNextMeeting(c.next_meeting_at)}{c.next_meeting_format && ` · ${c.next_meeting_format.replace('_', '-')}`}
                    </Typography>
                    {(c.next_meeting_format === 'online' || c.next_meeting_format === 'simulcast') && c.next_meeting_zoom_id && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Zoom · ID {c.next_meeting_zoom_id}{c.next_meeting_zoom_pass && ` · Passcode ${c.next_meeting_zoom_pass}`}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">No upcoming meetings</Typography>
                )}
              </Paper>
            ))}
          </Box>
        )
      ) : (
        oneOnOne.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">
              No 1:1 students. Students you lead-mentor outside any class would appear here.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.5 }}>
            {oneOnOne.map(s => (
              <Paper
                key={s.student_id}
                variant="outlined"
                sx={{ p: 2, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                onClick={() => navigate('/students')}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>{s.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Grade {s.grade ?? '—'} · {s.current_step ? `Step ${s.current_step}` : 'No project'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Last seen {timeSince(s.last_login)}
                </Typography>
              </Paper>
            ))}
          </Box>
        )
      )}
    </Box>
  )
}
